import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useParticipantsSummonsQuery } from './ParticipantsSummons.graphql';
import { ParticipantsSummonsTable } from './ParticipantsSummonsTable';
import { Alert } from '@startuptools/ui/Alert';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { CircularProgress } from '@startuptools/ui/CircularProgress';
import { IconButton } from '@startuptools/ui/IconButton';
import { Input } from '@startuptools/ui/Input';
import { Typography } from '@startuptools/ui/Typography';
import { GenericError } from '@startuptools/ui/Error';
import { Stack } from '@startuptools/ui/Stack';
import WarningIcon from '@mui/icons-material/Warning';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Trans, useLingui } from '@lingui/react';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from '@startuptools/common/react-hooks';
import { type GqlMeetingErrorsQuery, useFileQuery } from '../../../../graphql/react-operations';
import { compact, isNil, map, pick } from 'lodash-es';
import { GqlEmailSimplifiedState } from '../../../../graphql/base-types.graphql';

const INTERVAL = 10000;

export const ParticipantsSummons = ({
  meetingId,
  documentId,
  errors,
}: {
  meetingId: string | null | undefined;
  documentId: string | null | undefined;
  errors: GqlMeetingErrorsQuery['meetingErrors'] | null | undefined;
}) => {
  const { i18n } = useLingui();
  const companyId = useCompanyId();
  const [filter, setFilter] = useState<string>('');
  const debouncedFilter = useDebounce(filter, 500);
  const [failedEmailsOnly, setFailedEmailsOnly] = useState(false);
  const [pollInterval, setPollInterval] = useState(INTERVAL);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, error } = useParticipantsSummonsQuery({
    variables: {
      companyId,
      meetingId: meetingId ?? '',
      filter: { identity: { name: debouncedFilter.length > 0 ? `%${debouncedFilter}%` : null }, failedEmailsOnly },
      offset: rowsPerPage * page,
      limit: rowsPerPage,
    },
    skip: isNil(meetingId),
    pollInterval,
    fetchPolicy: 'cache-and-network',
  });

  const { data: documentData } = useFileQuery({
    variables: { id: documentId ?? '' },
    skip: isNil(documentId),
  });

  useEffect(() => {
    if (
      data?.meetingParticipants.models.some(
        p => p.latestMeetingParticipantEmail?.email.emailSimplifiedState === GqlEmailSimplifiedState.Pending,
      )
    ) {
      setPollInterval(INTERVAL);
    } else {
      setPollInterval(0);
    }
  }, [data?.meetingParticipants]);

  const hasErrors = useMemo(() => {
    if (isNil(errors)) {
      return false;
    }
    return compact(map(pick(errors, ['details', 'language', 'agenda', 'postalVote']), e => e?.hasError)).length > 0;
  }, [errors]);

  if (error) {
    console.error(error);
    return <GenericError />;
  }

  if (!meetingId) {
    return null;
  }

  const handleRowsPerPageChange = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
    setPage(0);
  };

  const filterLoading = debouncedFilter !== filter;

  return (
    <Stack gap={2}>
      {hasErrors && (
        <Alert color="warning" startDecorator={<WarningIcon />}>
          <Trans id="Finish all the preview steps before sending out the summons" />
        </Alert>
      )}
      {!hasErrors && (
        <>
          <Stack direction="row" alignItems="center" gap={2}>
            <Input
              startDecorator={
                !filterLoading ? <SearchIcon /> : <CircularProgress size="sm" color="neutral" variant="soft" />
              }
              endDecorator={
                filter.length > 0 && (
                  <IconButton onClick={() => setFilter('')} size="sm">
                    <ClearIcon />
                  </IconButton>
                )
              }
              fullWidth
              value={filter}
              placeholder={i18n._('Search...')}
              onChange={event => setFilter(event.target.value)}
            />
            <Checkbox
              label={
                <Typography noWrap>
                  <Trans id="Only show failed emails" />
                </Typography>
              }
              checked={failedEmailsOnly}
              onChange={() => setFailedEmailsOnly(!failedEmailsOnly)}
            />
          </Stack>
          <ParticipantsSummonsTable
            participants={data?.meetingParticipants.models ?? []}
            summonsDoc={documentData?.file}
            meetingId={meetingId}
            totalCount={data?.meetingParticipants.totalCount ?? 0}
            page={page}
            rowsPerPage={rowsPerPage}
            handlePageChange={num => setPage(num)}
            handleRowsPerPageChange={handleRowsPerPageChange}
          />
        </>
      )}
    </Stack>
  );
};
